import { defaultMarkingFor } from './marking'
import type { ComponentKind, Marking, Tracking } from './types'

/**
 * Inventory: categories, the quick "new item", and receiving a delivery.
 *
 * Div's brief, in his words: "engineers need to be able to create categories
 * for items, and then someone can say, oh, 22 cameras arrived." Everything here
 * serves one of those two sentences, and **ease of use over discipline** is the
 * tie-breaker — a receive that asks for nothing it cannot use.
 *
 * ## Receiving a labelled item before there is a printer
 *
 * A `qr_label` type was received by scanning a pre-printed Kronos label onto
 * each part (§10.3). There is no printer yet, so nobody can. The decision:
 *
 *  - **Kronos allocates the serial**, from the same `serial_pool` and under the
 *    same advisory lock that `receiveUnmarked` and `startUnit` use, with
 *    `printed_at` NULL. One index space, so it can never collide with a label
 *    printed later.
 *  - **The maker's serial is recorded as `supplier_serial`** — an attribute,
 *    never the key (§10.1), exactly what the column was for.
 *  - **The type keeps its marking.** `qr_label` still says the part *should*
 *    carry a label; the component is simply waiting for it. "Waiting" is
 *    derived — a marked type whose serial has `printed_at` NULL — and never
 *    stored, so printing it later (§10.5: "reprint is `print(serial)` with no
 *    state change") clears it without anything else to update.
 *
 * What it does not change: a *person* still never supplies a Kronos serial for
 * an unmarked type, and nothing here accepts one at all — whatever is scanned
 * or typed during a receive is the maker's number. The old scan-a-blank-label
 * loop at `/intake?serial=` keeps working for anyone holding printed labels.
 */

// --- categories -------------------------------------------------------------

export const MAX_CATEGORY_LENGTH = 40

export type CategoryRefusal = 'empty' | 'too_long' | 'taken'

/** A category name as it is stored: trimmed, inner whitespace collapsed. */
export function normaliseCategoryName(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ')
}

/**
 * May this name be used for a new category, or as the new name of `selfId`?
 *
 * Case-insensitive against the others, because "Camera" and "camera" side by
 * side in a picker is two places for the same thing and items split between
 * them. Renaming a category to a different capitalisation of itself is fine.
 */
export function checkCategoryName(
  raw: string,
  existing: readonly { readonly id: string; readonly name: string }[],
  selfId: string | null = null,
): { readonly ok: true; readonly name: string } | { readonly ok: false; readonly reason: CategoryRefusal } {
  const name = normaliseCategoryName(raw)
  if (name === '') return { ok: false, reason: 'empty' }
  if (name.length > MAX_CATEGORY_LENGTH) return { ok: false, reason: 'too_long' }
  const clash = existing.find((c) => c.id !== selfId && c.name.toLowerCase() === name.toLowerCase())
  if (clash) return { ok: false, reason: 'taken' }
  return { ok: true, name }
}

export function describeCategoryRefusal(reason: CategoryRefusal, name = ''): string {
  switch (reason) {
    case 'empty':
      return 'give the category a name'
    case 'too_long':
      return `keep a category name under ${MAX_CATEGORY_LENGTH} characters`
    case 'taken':
      return `there is already a category called "${normaliseCategoryName(name)}"`
  }
}

// --- a new item, the quick way ----------------------------------------------

/**
 * The question the quick form asks, in its own words: does each one have its
 * own serial number, or is it counted in bulk like screws? That *is* `tracking`;
 * the form just does not make anybody learn the word.
 */
export type HowTracked = 'each' | 'bulk'

/**
 * What a quick new item becomes. Bought — that is what arrives in a box — and
 * marked the way `defaultMarkingFor` marks a bought part, so the quick form and
 * the item editor can never disagree about the same choice.
 */
export function quickItemShape(how: HowTracked): {
  readonly kind: ComponentKind
  readonly tracking: Tracking
  readonly marking: Marking
} {
  const tracking: Tracking = how === 'each' ? 'serialized' : 'quantity'
  const kind: ComponentKind = 'buy'
  return { kind, tracking, marking: defaultMarkingFor({ tracking, kind }) }
}

/** A bulk unit, or "each" for anything counted one at a time. */
export function quickItemUnit(how: HowTracked, raw: string | undefined): string {
  if (how === 'each') return 'each'
  const unit = (raw ?? '').trim()
  return unit === '' ? 'each' : unit.slice(0, 16)
}

/** Can this item be taken in at Receive? Things we assemble are built, not delivered. */
export function isReceivable(item: { readonly kind: ComponentKind; readonly isRoot?: boolean }): boolean {
  return item.kind !== 'make_internal' && item.isRoot !== true
}

// --- receiving --------------------------------------------------------------

/** One delivery of one item. Mirrors `receiveUnmarked`'s ceiling. */
export const MAX_UNITS_PER_RECEIPT = 500
/** A bulk quantity this large is a typo for a smaller one, not a delivery. */
export const MAX_BULK_PER_RECEIPT = 1_000_000
export const MAX_NOTE_LENGTH = 500
const MAX_MAKER_SERIAL = 80

export interface ReceiptRequest {
  readonly tracking: Tracking
  /** How many arrived — units for a serialized item, the amount in `unit` for bulk. */
  readonly quantity: number
  /** The makers' own serials, scanned or typed, in order. Serialized items only. */
  readonly makerSerials?: readonly string[]
  readonly note?: string
}

export type ReceiptRefusal =
  | 'bad_quantity'
  | 'too_many'
  | 'whole_units'
  | 'counted_has_no_serials'
  | 'duplicate_maker_serial'
  | 'maker_serial_too_long'
  | 'note_too_long'

export interface ReceiptPlan {
  /** Units for a serialized item, the amount for bulk. */
  readonly quantity: number
  /** One entry per unit for a serialized item — its maker's serial or null. Empty for bulk. */
  readonly units: readonly (string | null)[]
  readonly note: string
}

/**
 * Turn what somebody entered at Receive into exactly what will be written.
 *
 * **More serials than the count raises the count.** Somebody who said 20 and
 * then scanned 22 boxes has just told us it was 22; refusing would make them
 * scroll back up to fix a number the scans already answered. Fewer is the
 * ordinary case — the rest get a Kronos number and no maker's serial.
 *
 * A repeated maker's serial *is* refused: the same box scanned twice is the
 * one mistake here that would put a part on the shelf that does not exist.
 */
export function planReceipt(
  req: ReceiptRequest,
): { readonly ok: true; readonly plan: ReceiptPlan } | { readonly ok: false; readonly reason: ReceiptRefusal; readonly detail: string } {
  const note = (req.note ?? '').trim()
  if (note.length > MAX_NOTE_LENGTH) {
    return { ok: false, reason: 'note_too_long', detail: `keep the note under ${MAX_NOTE_LENGTH} characters` }
  }
  const serials = (req.makerSerials ?? []).map((s) => s.trim()).filter((s) => s !== '')

  if (req.tracking === 'quantity') {
    if (serials.length > 0) {
      return { ok: false, reason: 'counted_has_no_serials', detail: 'a bulk item has no serial numbers to record' }
    }
    if (!Number.isFinite(req.quantity) || req.quantity <= 0) {
      return { ok: false, reason: 'bad_quantity', detail: 'say how much arrived' }
    }
    if (req.quantity > MAX_BULK_PER_RECEIPT) {
      return { ok: false, reason: 'too_many', detail: 'that is more than one delivery — check the number' }
    }
    return { ok: true, plan: { quantity: req.quantity, units: [], note } }
  }

  const seen = new Set<string>()
  for (const s of serials) {
    if (s.length > MAX_MAKER_SERIAL) {
      return { ok: false, reason: 'maker_serial_too_long', detail: `"${s.slice(0, 20)}…" is too long to be a serial number` }
    }
    const key = s.toUpperCase()
    if (seen.has(key)) {
      return { ok: false, reason: 'duplicate_maker_serial', detail: `${s} is in the list twice` }
    }
    seen.add(key)
  }
  if (!Number.isInteger(req.quantity) || (req.quantity < 1 && serials.length === 0)) {
    return {
      ok: false,
      reason: Number.isInteger(req.quantity) ? 'bad_quantity' : 'whole_units',
      detail: Number.isInteger(req.quantity) ? 'say how many arrived' : 'each one is a whole part — use a whole number',
    }
  }
  const quantity = Math.max(req.quantity, serials.length)
  if (quantity > MAX_UNITS_PER_RECEIPT) {
    return {
      ok: false,
      reason: 'too_many',
      detail: `receive up to ${MAX_UNITS_PER_RECEIPT} at a time — split a bigger delivery in two`,
    }
  }
  const units = Array.from({ length: quantity }, (_, i) => serials[i] ?? null)
  return { ok: true, plan: { quantity, units, note } }
}

/**
 * Is this unit still waiting for the mark its type says it carries?
 *
 * Derived from the pool, never stored: a marked type, and a serial nobody has
 * printed. Unmarked types are never waiting — nothing is ever put on them.
 */
export function markPending(marking: Marking, printed: boolean): boolean {
  return marking !== 'none' && !printed
}

/** What has to happen to a waiting unit, in the words of someone at the bench. */
export function pendingMarkLabel(marking: Marking): string | null {
  switch (marking) {
    case 'qr_label':
      return 'label to print'
    case 'text_only':
      return 'serial to write on'
    case 'none':
      return null
  }
}

// --- reading it back --------------------------------------------------------

/** A quantity in its unit: "22", "2.5 m", "500 g". `each` is implied, never shown. */
export function formatQuantity(quantity: number, unit: string): string {
  const n = Number.isInteger(quantity) ? String(quantity) : String(Math.round(quantity * 1000) / 1000)
  const u = unit.trim()
  return u === '' || u === 'each' ? n : `${n} ${u}`
}

/**
 * The done screen's sentence: "22 × Camera received · now 34 on the shelf".
 * A bulk item in a real unit reads "2.5 m of Cable received · now 12.5 m on the shelf".
 */
export function describeReceipt(r: {
  readonly name: string
  readonly quantity: number
  readonly unit: string
  readonly onShelf: number
}): string {
  const each = r.unit.trim() === '' || r.unit.trim() === 'each'
  const what = each ? `${formatQuantity(r.quantity, r.unit)} × ${r.name}` : `${formatQuantity(r.quantity, r.unit)} of ${r.name}`
  return `${what} received · now ${formatQuantity(r.onShelf, r.unit)} on the shelf`
}

export interface PickableItem {
  readonly id: string
  readonly name: string
  readonly category: string
  readonly partNumber: string
  readonly supplier: string
  /** ISO instant of the latest delivery, or null if none has been received. */
  readonly lastReceivedAt: string | null
}

/**
 * "What arrived?" — the list at the top of Receive, filtered and ordered.
 *
 * Every word typed must appear somewhere in the name, part number, supplier
 * or category, so "m4 screw" finds "Screw, M4 × 10". With nothing typed, the
 * most recently received come first: a delivery is usually more of something
 * that arrived before. While typing, a name that *starts* with the words beats
 * one that merely contains them, and a part-number hit sits after both — the
 * label on the box is more often the name than the number.
 */
export function pickItems<T extends PickableItem>(
  items: readonly T[],
  query: string,
  category: string | null = null,
): readonly T[] {
  const words = query.toLowerCase().split(/\s+/).filter((w) => w !== '')
  const inCategory = category === null ? items : items.filter((i) => i.category === category)
  const matching = inCategory.filter((i) => {
    const hay = `${i.name} ${i.partNumber} ${i.supplier} ${i.category}`.toLowerCase()
    return words.every((w) => hay.includes(w))
  })
  const recency = (i: T): number => (i.lastReceivedAt === null ? -Infinity : Date.parse(i.lastReceivedAt))
  const rank = (i: T): number => {
    if (words.length === 0) return 0
    const name = i.name.toLowerCase()
    const phrase = words.join(' ')
    if (name.startsWith(phrase)) return 0
    if (words.every((w) => name.includes(w))) return 1
    return 2
  }
  return [...matching].sort(
    (a, b) => rank(a) - rank(b) || recency(b) - recency(a) || a.name.localeCompare(b.name),
  )
}

/** The category chips above the list: each category in use, with how many items are in it. */
export function categoryCounts(items: readonly { readonly category: string }[]): readonly { readonly name: string; readonly count: number }[] {
  const counts = new Map<string, number>()
  for (const i of items) if (i.category !== '') counts.set(i.category, (counts.get(i.category) ?? 0) + 1)
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

// --- the shelf at a glance --------------------------------------------------

/**
 * How an item's shelf stands, in one word a tile can colour:
 *
 *  - `out` — nothing on the shelf. Said whether or not a minimum was set,
 *    because "none left" needs no threshold to be worth knowing.
 *  - `low` — at or below the item's minimum (`safetyStock`, set on the item in
 *    Design). At, not only below: the minimum is the floor you reorder *on*,
 *    the way Sortly's and inFlow's "min level" alert both fire.
 *  - `ok` — anything else, including an item with no minimum set.
 *
 * Only for things that are received. An assembly is built, so an empty shelf
 * of arms is a build to plan, not a delivery to chase, and is never `out`.
 */
export type StockLevel = 'out' | 'low' | 'ok'

export interface ShelfFigures {
  readonly onShelf: number
  readonly fitted: number
  /** The item's minimum, or null when nobody has set one. Zero means none. */
  readonly safetyStock: number | null
  readonly receivable: boolean
}

export function stockLevel(item: ShelfFigures): StockLevel {
  if (!item.receivable) return 'ok'
  if (item.onShelf <= 0) return 'out'
  if (item.safetyStock !== null && item.safetyStock > 0 && item.onShelf <= item.safetyStock) return 'low'
  return 'ok'
}

/**
 * The little bar under a tile's number — what it is measured against, and
 * how full it is (0..1).
 *
 *  - With a minimum, against **twice the minimum**, so the minimum always sits
 *    at the halfway mark (`mark: 0.5`) and "below the line" reads at a glance.
 *  - Without one, as the **share of all we have that is still on the shelf**
 *    rather than fitted: 22 on the shelf and none fitted is a full bar, 2 on
 *    the shelf and 14 fitted is a sliver. Nothing at all is an empty bar.
 */
export function stockMeter(item: ShelfFigures): {
  readonly fraction: number
  readonly basis: 'minimum' | 'fitted'
  readonly mark: number | null
} {
  const clamp = (n: number): number => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0)
  if (item.safetyStock !== null && item.safetyStock > 0) {
    return { fraction: clamp(item.onShelf / (2 * item.safetyStock)), basis: 'minimum', mark: 0.5 }
  }
  const total = Math.max(0, item.onShelf) + Math.max(0, item.fitted)
  return { fraction: total === 0 ? 0 : clamp(item.onShelf / total), basis: 'fitted', mark: null }
}

export type InventorySort = 'name' | 'shelf' | 'recent'

/**
 * The three orders Inventory offers. Ties always fall back to the name, so a
 * list never reshuffles between two renders of the same data.
 *
 *  - `name` — A to Z, for finding a thing you can name;
 *  - `shelf` — most on the shelf first. Bulk amounts and unit counts are
 *    compared as numbers; that is the honest reading of "most", and a person
 *    who wants only cameras has the category chip for it;
 *  - `recent` — the latest delivery first, never-received last.
 */
export function sortInventory<T extends { readonly name: string; readonly onShelf: number; readonly lastReceivedAt: string | null }>(
  items: readonly T[],
  sort: InventorySort,
): readonly T[] {
  const byName = (a: T, b: T): number => a.name.localeCompare(b.name)
  const at = (i: T): number => (i.lastReceivedAt === null ? -Infinity : Date.parse(i.lastReceivedAt))
  switch (sort) {
    case 'shelf':
      return [...items].sort((a, b) => b.onShelf - a.onShelf || byName(a, b))
    case 'recent':
      return [...items].sort((a, b) => at(b) - at(a) || byName(a, b))
    case 'name':
      return [...items].sort(byName)
  }
}

/** How far back "received this week" looks: the last seven days, not since Monday. */
export const RECENT_DAYS = 7

export interface InventorySummary {
  /** Items that can be received — the things on the shelves. */
  readonly items: number
  /** Serialized units on the shelf. Bulk amounts are not added in: 1,986 screws are not 1,986 "units". */
  readonly unitsOnShelf: number
  /** Bulk items with anything on the shelf. */
  readonly bulkInStock: number
  /** Deliveries in the last `RECENT_DAYS`, and how many different items they were. */
  readonly deliveriesThisWeek: number
  readonly itemsThisWeek: number
  /** Units waiting for a Kronos label or a written serial. */
  readonly labelBacklog: number
  readonly low: number
  readonly out: number
}

/**
 * The strip across the top of Inventory. `now` is a parameter — "what did last
 * week look like" is the same call with a different instant.
 *
 * Rolling seven days rather than "since Monday": the server and the person
 * reading it can be in different time zones, and a week that starts at a
 * different hour for each is two answers to one question.
 */
export function summariseInventory(
  items: readonly (ShelfFigures & { readonly id: string; readonly tracking: Tracking; readonly labelsPending: number })[],
  receipts: readonly { readonly itemId: string; readonly receivedAt: string }[],
  now: string,
): InventorySummary {
  const shelved = items.filter((i) => i.receivable)
  const since = Date.parse(now) - RECENT_DAYS * 24 * 60 * 60 * 1000
  const until = Date.parse(now)
  const recent = receipts.filter((r) => {
    const t = Date.parse(r.receivedAt)
    return t > since && t <= until
  })
  const levels = shelved.map(stockLevel)
  return {
    items: shelved.length,
    unitsOnShelf: shelved.filter((i) => i.tracking === 'serialized').reduce((n, i) => n + Math.max(0, i.onShelf), 0),
    bulkInStock: shelved.filter((i) => i.tracking === 'quantity' && i.onShelf > 0).length,
    deliveriesThisWeek: recent.length,
    itemsThisWeek: new Set(recent.map((r) => r.itemId)).size,
    labelBacklog: items.reduce((n, i) => n + Math.max(0, i.labelsPending), 0),
    low: levels.filter((l) => l === 'low').length,
    out: levels.filter((l) => l === 'out').length,
  }
}

// --- Correcting a count -----------------------------------------------------

/**
 * "They said five cameras; there are seven." A correction, never an overwrite.
 *
 * Kronos has no stock number to edit: a camera on the shelf is a row with a
 * serial, and screws on the shelf are the sum of an append-only ledger. So a
 * correction is a recorded event that makes the derived figure come out right:
 *
 *  - **bulk** — one `adjusted` ledger entry for the difference;
 *  - **serialized, too few** — that many units added, serials from the pool;
 *  - **serialized, too many** — that many units taken off, **newest first and
 *    only ones nothing has happened to**. A unit that has been fitted, worked
 *    on or written up has a history, and a miscount is not a reason to bury it;
 *    if the surplus can only come from those, the correction is refused and
 *    says which way to go instead.
 */
export interface CorrectionCandidate {
  readonly componentId: string
  readonly receivedAt: string
  /** Ever installed, worked on, or written up. */
  readonly used: boolean
}

export type CountCorrection =
  | { readonly ok: true; readonly kind: 'none' }
  | { readonly ok: true; readonly kind: 'adjust'; readonly delta: number }
  | { readonly ok: true; readonly kind: 'add'; readonly quantity: number }
  | { readonly ok: true; readonly kind: 'remove'; readonly componentIds: readonly string[] }
  | { readonly ok: false; readonly reason: 'bad_count' | 'whole_units' | 'too_many' | 'units_have_history'; readonly detail: string }

export function planCountCorrection(input: {
  readonly tracking: Tracking
  readonly onShelf: number
  readonly counted: number
  /** Serialized only: the units on the shelf now. */
  readonly candidates?: readonly CorrectionCandidate[]
}): CountCorrection {
  const { tracking, onShelf, counted } = input
  if (!Number.isFinite(counted) || counted < 0) {
    return { ok: false, reason: 'bad_count', detail: 'Type how many are on the shelf — zero or more.' }
  }
  if (counted === onShelf) return { ok: true, kind: 'none' }

  if (tracking === 'quantity') {
    if (Math.abs(counted - onShelf) > MAX_BULK_PER_RECEIPT) {
      return { ok: false, reason: 'too_many', detail: `That is more than ${MAX_BULK_PER_RECEIPT.toLocaleString()} off — check the number.` }
    }
    return { ok: true, kind: 'adjust', delta: counted - onShelf }
  }

  if (!Number.isInteger(counted)) {
    return { ok: false, reason: 'whole_units', detail: 'Each one has its own serial, so count whole ones.' }
  }
  if (counted > onShelf) {
    const quantity = counted - onShelf
    if (quantity > MAX_UNITS_PER_RECEIPT) {
      return { ok: false, reason: 'too_many', detail: `That adds more than ${MAX_UNITS_PER_RECEIPT} — receive them as a delivery instead.` }
    }
    return { ok: true, kind: 'add', quantity }
  }

  const surplus = onShelf - counted
  const unused = (input.candidates ?? [])
    .filter((c) => !c.used)
    .sort((a, b) => (a.receivedAt < b.receivedAt ? 1 : a.receivedAt > b.receivedAt ? -1 : a.componentId < b.componentId ? 1 : -1))
  if (unused.length < surplus) {
    return {
      ok: false,
      reason: 'units_have_history',
      detail:
        unused.length === 0
          ? 'Every one on the shelf has a history, so none can be taken off as a miscount. Scrap the missing ones from their own pages.'
          : `Only ${unused.length} of them have never been used. The rest have a history — scrap those from their own pages.`,
    }
  }
  return { ok: true, kind: 'remove', componentIds: unused.slice(0, surplus).map((c) => c.componentId) }
}

/** "+2 will be added", "3 will be taken off" — the sentence before Save. */
export function describeCorrection(c: CountCorrection, unit = ''): string {
  if (!c.ok) return c.detail
  if (c.kind === 'none') return 'That matches what Kronos has.'
  if (c.kind === 'adjust') return `${c.delta > 0 ? '+' : '−'}${formatQuantity(Math.abs(c.delta), unit)} will be recorded as a count correction.`
  if (c.kind === 'add') return `${c.quantity} will be added, each with a new Kronos serial.`
  return `${c.componentIds.length} will be taken off — the newest ones, never used.`
}
